import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import _ from 'lodash';
import { Box, CircularProgress } from '@material-ui/core';
import { List, CellMeasurer, CellMeasurerCache, AutoSizer, InfiniteLoader, WindowScroller } from 'react-virtualized';
import { Search } from '..';
import useUpdateEffect from '../../../../utils/useUpdateEffect';
import { ehandoverAPI } from '../../../../apis';
import { getArrayRandomKey } from '../../../../utils';
import NewNote from './newNote';

const useStyles = makeStyles(() => ({
  note: {
    '&>div:first-child': {
      borderRadius: '0 0 0.9375rem 0.9375rem',
      '&>div:first-child': {
        borderRadius: '0 0 0.9375rem 0.9375rem',
      },
    },
  },
}));

const initSearch = (search) => (search === '' ? null : search);

const initTag = (tag) => (tag.length > 0 ? tag : null);
const EMPTY_TASK_CHECKLIST = [];



export default function NewNotes({
  notes, // it is the core list
  wardStaffdata,
  type,
  getTaskListAPI,
  next,
  deleteTaskAPI,
  pinTaskAPI,
  search,
  setSearch,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  tagList,
  selectedTags,
  setSelectedTags,
  filterAPI,
  followUpFilter,
  handleFollowUpFilter,
  followUpTotal,
  refreshChecklistTabs,
  handleReset,
  sortOrder,
  setSortOrder,
  quickDays,
  setQuickDays,
  accessControl,
  individualUsers,
  setPendingAction,
  staffList,
  requiredAcknowLedge,
  setRequiredAcknowLedge,
  setHideCompletedItem,
  isHideCompletedItem,
  showHideCompleteItem,
  scrollableRef
}) {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [detailNotesList, setDetailNotesList] = useState([]);
  const nextPageFlagRef = useRef(false);
  const getRequestParamsRef = useRef(null);
  const getTaskListAPIRef = useRef(getTaskListAPI);
  const filterAPIRef = useRef(filterAPI);
  const refreshChecklistTabsRef = useRef(refreshChecklistTabs);
  const deleteTaskAPIRef = useRef(deleteTaskAPI);
  const pinTaskAPIRef = useRef(pinTaskAPI);
  const typeRef = useRef(type);
  const startDateRef = useRef(startDate);
  const endDateRef = useRef(endDate);
  const loginInfo = useSelector((state) => (state.loginInfo));
  const userRole = useSelector((state) => (state.userRole));
  const sinkCompletedToBottom = useSelector((state) => (state.ehdSetting.sinkCompletedChecklist));
  const windowScrollerRef = useRef(null);
  const cache = useRef(
    new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: 380,
      keyMapper: (rowIndex) => rowIndex,
    }));

  const listRef = useRef(null);
  const rootElementRef = useRef(null);
  const [scrollableContainer, setScrollableContainer] = useState(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  getTaskListAPIRef.current = getTaskListAPI;
  filterAPIRef.current = filterAPI;
  refreshChecklistTabsRef.current = refreshChecklistTabs;
  deleteTaskAPIRef.current = deleteTaskAPI;
  pinTaskAPIRef.current = pinTaskAPI;
  typeRef.current = type;
  startDateRef.current = startDate;
  endDateRef.current = endDate;

  const handleVerification = (start, end) => {
    return Boolean(
      (moment(start, '20yy-MM-DD HH:mm', true).isValid() &&
        moment(end, '20yy-MM-DD HH:mm', true).isValid() &&
        moment(end).isSameOrAfter(start, 'days')) ||
        (moment(start, '20yy-MM-DD HH:mm', true).isValid() && !moment(end, '20yy-MM-DD HH:mm', true).isValid()) ||
        (!moment(start, '20yy-MM-DD HH:mm', true).isValid() && moment(end, '20yy-MM-DD HH:mm', true).isValid())
    );
  };

  // 统一整理当前 tab 请求参数，避免首屏加载、搜索和分页各自拼一遍。
  const getRequestParams = useCallback(() => {
    return getRequestParamsRef.current?.({}) ?? {};
  }, []);

  // 支持在 search 输入框里先本地编辑，只有点击搜索/回车时才把关键词提交到页面层。
  const createRequestParams = useCallback(
    ({
      searchValue = search,
      selectedTagsValue = selectedTags,
      startDateValue = startDate,
      endDateValue = endDate,
    } = {}) => {
      const tags = _.map(selectedTagsValue, (item) => item?.tagName);
      let params = {
        search: initSearch(searchValue),
        tag: initTag(tags),
      };

      if (handleVerification(startDateValue, endDateValue)) {
        params.startDate = startDateValue;
        params.endDate = endDateValue;
      }

      return params;
    },
    [endDate, search, selectedTags, startDate]
  );
  getRequestParamsRef.current = createRequestParams;
  const loadCurrentTabList = useCallback(async () => {
    await getTaskListAPIRef.current(getRequestParams());
  }, [getRequestParams]);

  // 当前 tab 首次挂载时，只加载当前 tab 自己的 list。
  useEffect(() => {
    if (handleVerification(startDate, endDate)) {
      (async () => {
        await loadCurrentTabList();
      })();
    }
  }, []);

  // 公共筛选变化后，走页面级 filterAPI，由页面决定“当前 list + 哪些 total 需要联动刷新”。
  useUpdateEffect(async () => {
    if (handleVerification(startDate, endDate)) {
      (async () => {
        await handleSearch();
      })();
    }
  }, [startDate, endDate, sortOrder, selectedTags, requiredAcknowLedge]);

  // 通过 ref 读取最新筛选条件，避免筛选输入变化时每条 NewNote 都因为回调引用变化重渲染。
  const updateFollowTotal = useCallback(() => {
    if (!refreshChecklistTabsRef.current) {
      return;
    }
    const params = getRequestParamsRef.current();
    if (handleVerification(startDateRef.current, endDateRef.current)) {
      refreshChecklistTabsRef.current({
        ...params,
        startDate: startDateRef.current,
        endDate: endDateRef.current,
      });
    } else {
      refreshChecklistTabsRef.current(params);
    }
  }, []);

  const handleSearch = useCallback(async (start) => {
    const isLoadMore = Boolean(start);
    if (isLoadMore && nextPageFlagRef.current) {
      return;
    }
    try {
      setLoading(true);
      let params = getRequestParamsRef.current();

      // 分页时只加载当前 tab 的下一页，不触发其它 tab 的 total / list 刷新。
      if (isLoadMore) {
        nextPageFlagRef.current = true;
        params.start = start;
        await getTaskListAPIRef.current(params);
        return;
      }

      await filterAPIRef.current(params);
    } catch (error) {
    } finally {
      if (isLoadMore) {
        nextPageFlagRef.current = false;
      }
      setLoading(false);
    }
  }, []);

  const handleSubmitSearch = useCallback(
    async (nextSearch) => {
      const searchValue = nextSearch ?? '';
      setSearch(searchValue);
      try {
        setLoading(true);
        await filterAPIRef.current(getRequestParamsRef.current({ searchValue }));
      } catch (error) {
      } finally {
        setLoading(false);
      }
    },
    [setSearch]
  );

  // followUpFilter 只影响 checklist 当前 tab，保持原来的单 tab 刷新逻辑。
  const searchFollowUp = useCallback(async () => {
    try {
      await getTaskListAPIRef.current(getRequestParamsRef.current());
    } catch (error) {
      console.log(error);
    }
  }, []);

  useUpdateEffect(async () => {
    (async () => {
      await searchFollowUp();
    })();
  }, [followUpFilter]);

  const handleSearchRef = useRef(handleSearch);
  handleSearchRef.current = handleSearch;

  const handlePin = useCallback(({ taskId, pin, isManualUnpin, pinExpiryDate }) => {
    pinTaskAPIRef.current({
      taskId,
      pin,
      reLoad: handleSearchRef.current,
      isManualUnpin,
      pinExpiryDate,
});
  }, []);

  const handleDelete = useCallback((TASK_ID, name, deleteOptions) => {
    deleteTaskAPIRef.current(TASK_ID, name, handleSearchRef.current, typeRef.current, deleteOptions);
  }, []);

  useEffect(() => {
    const root = scrollableRef?.current;
    if (!root) return;

    rootElementRef.current = root;
    setScrollableContainer(root)

    const onScroll = _.throttle(() => {
      const { scrollTop, scrollHeight, clientHeight } = root ;
      if (scrollTop + clientHeight >= scrollHeight - 5) {
        if (!loading && next) {
          (async () => {
            await handleSearchRef.current(next);
          })();
        }
      }
    }, 100);

    root.addEventListener('scroll', onScroll);
    return () => {
      root.removeEventListener('scroll', onScroll);
      rootElementRef.current = null;
    };
  }, [next, loading,scrollableRef]);

  // new get noteList API
  const getNewTaskItemListAPI = useCallback(async function fetchTaskItemList(
    apiTaskIdList, // List of task IDs
    order = { ID: 'asc', TOGGLED_DATE: 'asc', IS_COMPLETE: 'desc', SORT_INDEX: 'asc' },
    retries = 3,
    delay = 1000 // Initial delay for retries
  ) {
    if (_.isEmpty(apiTaskIdList)) {
      return;
    }
    try {
      const { result } = await ehandoverAPI.getNewTaskItemList(
        { order, taskIdList: apiTaskIdList },
        { isHideLoading: true }
      );
      const groupList = result.reduce((acc, item) => {
        const key = item.TASK_ID;
        acc[key] = acc[key] || [];
        acc[key].push(item);
        return acc;
      }, {});
      setDetailNotesList((item) => ({ ...item, ...groupList }));
    } catch (error) {
      console.error('API call failed:', error);
      if (retries > 0) {
        await new Promise((resolve) => setTimeout(resolve, delay));
        return fetchTaskItemList(apiTaskIdList, order, retries - 1, delay * 2);
      }
      console.error('Failed to fetch task item list after maximum retries');
    }
  }, []);

  const fetchTaskItems = useMemo(
    () =>
      _.debounce(async (arr) => {
        try {
          await getNewTaskItemListAPI(arr);
        } catch (error) {
        }
      }, 300),
    [getNewTaskItemListAPI]
  );

  useEffect(() => {
    return () => {
      fetchTaskItems.cancel();
    };
  }, [fetchTaskItems]);

  useEffect(() => {
    if (!_.isEmpty(notes)) {
      const slicedNotes = notes.slice(Math.max(0, notes.length - 50), notes.length);
      const taskIds = slicedNotes.map((e) => Number(e.TASK_ID));

      fetchTaskItems(taskIds);
    }
  }, [notes, fetchTaskItems]);

  const handleResize = useMemo(() => 
    _.throttle(() => {
      if (windowScrollerRef?.current && listRef?.current && cache?.current) {
        // Clear cached heights since container width changed
        cache.current.clearAll(); 
        // Force WindowScroller to find its new page position
        windowScrollerRef.current.updatePosition(); 
        // Force the List to recalculate heights based on new width
        listRef.current.recomputeRowHeights(); 
      }
    }, 200, {trailing: true,leading: false}), // Fires at most once every 200ms
    [windowScrollerRef, listRef, cache]
  );

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    
    // Clean up listener and cancel pending throttles on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      handleResize.cancel();
    };
  }, [handleResize]);

  useEffect(() => {
    handleResize();
  },[isHideCompletedItem]);

  const customVirtualizationOverscanGetter = useCallback(({
    cellCount,
    overscanCellsCount,
    startIndex,
    stopIndex,
  }) => {
    // 1. Core Bi-directional overscan around current viewport
    let overscanStartIndex = startIndex - overscanCellsCount;
    let overscanStopIndex = stopIndex + overscanCellsCount;
  
    // 2. Only anchor to focus if it is within a reasonable proximity cushion (e.g., 30 rows)
    if (focusedIndex >= 0) {
      const PROXIMITY_LIMIT = 100; 
      
      // focusedIndex is ABOVE the viewport (focusedIndex < startIndex)
      // Example: startIndex is 100, focusedIndex is 80. 100 - 80 = 20 (<= 30) -> True
      const isNearTop = focusedIndex < startIndex && (startIndex - focusedIndex) <= PROXIMITY_LIMIT;
      
      // focusedIndex is BELOW the viewport (focusedIndex > stopIndex)
      // Example: stopIndex is 150, focusedIndex is 170. 170 - 150 = 20 (<= 30) -> True
      const isNearBottom = focusedIndex > stopIndex && (focusedIndex - stopIndex) <= PROXIMITY_LIMIT;
      
      // focusedIndex is INSIDE the visible viewport
      const isInsideView = focusedIndex >= startIndex && focusedIndex <= stopIndex;

  
      if (isInsideView || isNearTop || isNearBottom) {
        // Expand buffer safely without dragging from the opposite end of the entire list
        overscanStartIndex = Math.min(overscanStartIndex, focusedIndex - overscanCellsCount);
        overscanStopIndex = Math.max(overscanStopIndex, focusedIndex + overscanCellsCount);
      }
    }
  
    // 3. Keep within absolute array boundaries
    return {
      overscanStartIndex: Math.max(0, overscanStartIndex),
      overscanStopIndex: Math.min(cellCount - 1, overscanStopIndex),
    };
  }, [focusedIndex]);

  const rowRenderer = useCallback(
    ({ index, key, style, parent }) => {
      const item = notes[index];
      const taskCheckList = detailNotesList[item.TASK_ID] ?? EMPTY_TASK_CHECKLIST;
      if (!item) return null;
      const uniqueKey = item.TASK_ID ? `new_notes_${item.TASK_ID}` : key; 
      return (
        <CellMeasurer
          cache={cache?.current}
          columnIndex={0}
          rowIndex={index}
          parent={parent}
          key={uniqueKey}
        >
          {({ measure, registerChild }) => {
            return (
            
            <div style={style}>
              <NewNote
                key={uniqueKey}
                id={uniqueKey}
                wardStaffdata={wardStaffdata}
                deleteTaskAPI={handleDelete}
                pinTaskAPI={handlePin}
                {...item}
                type={type}
                handleSearch={handleSearch}
                itemData={item.metadata}
                updateFollowTotal={updateFollowTotal}
                taskCheckList={taskCheckList}
                getNewTaskItemListAPI={getNewTaskItemListAPI}
                accessControl={accessControl}
                individualUsers={individualUsers}
                setPendingAction={setPendingAction}
                staffList={staffList}
                loginInfo={loginInfo}
                userRole={userRole}
                sinkCompletedToBottom={sinkCompletedToBottom}
                isHideCompletedItem={isHideCompletedItem}
                onHeightChange={measure}
                setFocusedIndex={() => {setFocusedIndex(index);}}
                resetFocusedIndex={() => {setFocusedIndex(-1);}}
              />
            </div>
          )}}
        </CellMeasurer>
      );
    },
    [
      cache,
      notes,
      detailNotesList,
      wardStaffdata,
      type,
      handleSearch,
      updateFollowTotal,
      getNewTaskItemListAPI,
      accessControl,
      individualUsers,
      setPendingAction,
      staffList,
      loginInfo,
      userRole,
      sinkCompletedToBottom,
      isHideCompletedItem,
      handleDelete,
      handlePin,
    ]
  );

  return (
    <div>
      <Search
        handleSubmitSearch={handleSubmitSearch}
        search={search}
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        type={type}
        tagList={tagList}
        selectedTags={selectedTags}
        setSelectedTags={setSelectedTags}
        followUpFilter={followUpFilter}
        handleFollowUpFilter={handleFollowUpFilter}
        followUpTotal={followUpTotal}
        handleReset={handleReset}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        quickDays={quickDays}
        setQuickDays={setQuickDays}
        requiredAcknowLedge={requiredAcknowLedge}
        setRequiredAcknowLedge={setRequiredAcknowLedge}
        setHideCompletedItem={setHideCompletedItem}
        isHideCompletedItem={isHideCompletedItem}
        showHideCompleteItem={showHideCompleteItem}
      />
      {loading && notes.length === 0 ? (
        <Box width="100%" display="flex" justifyContent="center" alignItems="center" p="1.25rem 0">
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <div className={classes.note}>
        {

          scrollableContainer && cache?.current && (
            <WindowScroller  ref={windowScrollerRef} scrollElement={scrollableContainer}>
            {({ height,  isScrolling, onChildScroll, scrollTop}) =>
              {
                return (
              <AutoSizer disableHeight>
                {({ width}) => {
                  return (
                      <List
                        ref={listRef}
                        width={width}
                        height={height || 500}
                        rowCount={notes.length}
                        rowHeight={cache?.current?.rowHeight}
                        rowRenderer={rowRenderer}
                        overscanRowCount={4}
                        isScrolling={isScrolling}
                        onScroll={onChildScroll}
                        autoHeight
                        estimatedRowSize={500}
                        scrollTop={scrollTop}
                        deferredMeasurementCache={cache?.current} 
                        overscanIndicesGetter={customVirtualizationOverscanGetter}
                      />
                );}}
              </AutoSizer>
                
              )}
            }
          </WindowScroller>

          )
        }
           
         
          {loading && (
            <Box width="100%" display="flex" justifyContent="center" alignItems="center" p="1.25rem 0">
              <CircularProgress color="primary" />
            </Box>
          )}
        </div>
      )}
    </div>
  );
}
